import React from 'react';

export const LIST = [
  {
    "header": "Referral",
    "list": [
      {
        "header": "💸 Give a referral bonus to friends",
        "idea": "Give friends a referral bonus when they recommend you to a new client. Sometimes a small incentive can go a long way. It's a tried and tested strategy that companies have been using for years.",
        "actionableTip": "Next time you speak to a friend about your work, ask them if they know of any potential clients. Tell them you're happy to give them 15% of the project fee as a thank you when the project closes. Sometimes friends don't want to receive an incentive. In this case, tell them you can give the client 15% off your normal fee."
      },
      {
        "header": "👷‍♀️ Reach out to former co-workers",
        "idea": "You've often built great working relationships with past co-workers. They exist in different networks to yours and may often know of someone looking to hire. They're usually happy to refer you if you ask them.",
        "actionableTip": "Look through your LinkedIn or contact book and find 10 former co-workers you've worked with. Catch-up with them and let them know what you've been recently doing. Ask them if they've heard of anyone hiring or to keep you in mind in the future. Combine this with a referral fee for best results."
      },
      {
        "header": "💼 Reach out to past clients",
        "idea": "If you've worked with a few clients over the years, reach out to a few you haven't spoken to in a while and ask if they have any new work for you. They may have forgotten about your services. Because you've worked for them in the past, you're more likely to close a deal.",
        "example": "",
        "actionableTip": "Scan through your emails in reverse chronological order beginning from 18 months ago. Send a short hello email to any old clients you find and ask them if they have any upcoming projects planned."
      },
      {
        "header": "✅ Ask for a referral after a successful project",
        "idea": "After successfully completing a project for a client, ask if they can refer you to someone else that might have a project. Asking after successfully completing a project increases the chances of someone actually referring you because you've been vetted by them.",
        "example": "",
        "actionableTip": "Arrange an end of project call with your client and ask them if they can introduce you to anyone else. You can even mention a referral bonus if you feel comfortable."
      },
      {
        "header": "🌐 Create a partnership network",
        "idea": "Create a multi-skilled partnership network of other freelancers. When a client asks for a service that you do not offer, refer them to someone in your partnership network so that others will do the same for you.",
        "example": "",
        "actionableTip": "Make a list of people in your current circle with complementary skills (copywriting, design, marketing etc) that you can get one of these networks up and running with. Get in touch and work out an agreement that is fair to both parties."
      },
      {
        "header": "🤝 Partner with an agency",
        "idea": "Partner with an agency that compliments your skillset (e.g. design if you're a developer) to increase your referrals. Agencies typically receive requests from clients for other types of work which they sometimes do not do. Consequently, agencies keep a list of trusted third-party services they can introduce clients to.",
        "actionableTip": "Message or email small agencies via LinkedIn or Google searches to ask if they would partner with you. You could sweeten the deal by offering a referral deal or also sending work their way which is out of your scope of services."
      },
      {
        "header": "👫 Reach out to friends"
      },
      {
        "header": "👪 Reach out to family"
      },
      {
        "header": "➕ Ask existing clients for more work"
      },
      {
        "header": "📜 Ask your client for a testimonial"
      },
      {
        "header": "🤝 Partner with an agency"
      }
    ]
  },
  {
    "header": "Online Communities",
    "list": [
      {
        "header": "🔍 Use Twitter's advanced search",
        "idea": "Work is often advertised on Twitter but only to their following. Use Twitter's advanced search tool, search for relevant keywords like \"hiring freelancer\" to find tweets advertising for work and reply to them.",
        "actionableTip": "Head over to <a href='https://twitter.com/search-advanced' target='blank'>https://twitter.com/search-advanced</a> and enter relevant keywords such as \"freelance\", \"web developer\", \"hiring\" or whatever is relevant to your service. Reply to recent relevant tweets."
      },
      {
        "header": "🔺 Checkout whoishiring on Hacker News",
        "idea": "The \"whoishiring\" account creates two threads every month on Hacker News. One for those looking for work (\"Ask HN: Who wants to be hired?\") and one for those hiring (\"Ask HN: Freelancer? Seeking freelancer?\"). It's community that receives a lot of attention.",
        "actionableTip": "Head over to <a href='https://news.ycombinator.com/submitted?id=whoishiring' target='blank'>https://news.ycombinator.com/submitted?id=whoishiring</a> and leave a comment on the latest \"Ask HN: Who wants to be hired?\" and \"Ask HN: Freelancer? Seeking freelancer?\" thread every month."
      },
      {
        "header": "🔗 Share your client's testimonials",
        "idea": "Share a great testimonial about your work on all your social media platforms. You may have testimonials on your website but there are more eyes on social media. It's a great way to build trust amongst your followers.",
        "actionableTip": "Extract 140 character or less from a client testimonial and convert it into a shareable image with your client's name listed underneath. Share it on all your social media platforms with a short thank you caption e.g. \"Thanks @client for your amazing feedback\"."
      },
      {
        "header": "🗯️ Contribute to interesting discussions",
        "idea": "When you see an interesting discussion online, add your thoughts and contribute to the discussion instead of just reading the thread. As you add value online you build up your online reputation and other readers may click on your profile from the discussion and discover your services.",
        "actionableTip": "Browse through Twitter or Facebook Groups and look for interesting discussion. Add your thoughts to the discussion by replying to someone else's comment. Try to make this habit when you browse social media."
      },
      {
        "header": "❓ Answer relevant questions on Quora",
        "idea": "Subscribe to relevant topics on Quora and add your own answer to common questions on the platform. Quora is a popular destination for questions and indexed well by Google. It can really help to get your name out there and drive traffic to your website.",
        "example": "",
        "actionableTip": "Sign up for a Quora account and search for common questions your clients have asked you like \"how can I add my Instagram to my website?\". Add your best answer to the question. As you answer more questions Quora will recommend you related questions to answer."
      },
      {
        "header": "💈 Append \"Open for work\" to your social media name",
        "idea": "Let your social media followers know that you're looking for work. People are busy and often engrossed in their own lives. This is a small reminder to people of what you do and you're looking for work every time to post.",
        "example": "",
        "actionableTip": "Append \"(Open for work)\" to the end of your Twitter & Instagram names. On LinkedIn update your job description to \"Freelancer - Open for work\". Now every time you post, reader know that you're seeking new clients."
      },
      {
        "header": "📰 Join relevant subreddits on reddit.com",
        "idea": "Join and engage in relevant subreddits on reddit.com. Being one of the largest communities in the world, subreddits are updated daily with job listings. Contribute to the community for best results.",
        "example": "",
        "actionableTip": `Browse and join the following subreddits. Post about yourself if possible:
          <br />
          <br />
          <ul>
          <li><a href="https://reddit.com/r/forhire" target="_blank">reddit.com/r/forhire</li>
          <li><a href="https://reddit.com/r/webdev" target="_blank">reddit.com/r/webdev</li>
          <li><a href="https://reddit.com/r/beermoney" target="_blank">reddit.com/r/beermoney</a></li>
          <li><a href="https://reddit.com/r/digitalnomad" target="_blank">reddit.com/r/digitalnomad</a></li>
          <li><a href="https://reddit.com/r/WorkOnline" target="_blank">reddit.com/r/WorkOnline</a></li>
          <li><a href="https://reddit.com/r/workathome" target="_blank">reddit.com/r/workathome</a></li>
          <li><a href="https://reddit.com/r/RemoteJobr" target="_blank">reddit.com/r/RemoteJobr</a></li>
          <li><a href="https://reddit.com/r/remotepython" target="_blank">reddit.com/r/remotepython</a></li>
          <li><a href="https://reddit.com/r/skipthedrive" target="_blank">reddit.com/r/skipthedrive</a></li>
          <li><a href="https://reddit.com/r/remotejs" target="_blank">reddit.com/r/remotejs</a></li>
          <li><a href="https://reddit.com/r/remotephp"  target="_blank">reddit.com/r/remotephp<a/></li>
</ul>`
      },
      {
        "header": "🔝 Promote your popular material via ads",
        "idea": "Pay to promote one of your popular social media posts on the same platform. Most popular social media platforms allow drive more traffic to your post by promoting it to a wider audience. You can even target specific types of clients you're looking for.",
        "actionableTip": "Browse through your social media accounts and find your top three most liked posts. Pay $20 to promote each to a larger audience and analyze which perform well. If any do, consider investing more money."

      },
      {
        "header": "👋 Engage with relevant Slack groups",
        "idea": "Join & engage in popular Slack Groups. Slack has quickly become a popular platform for engaging communities. However, they're like speakeasies.  They're not the easiest to find and often require referrals to join.",
        "actionableTip" : "Search for relevant communities on <a href='https://slofile.com' target='blank'>https://slofile.com</a> or ask fellow freelancers what groups they're part of. Once you've joined them, introduce yourself in the \"introductions\" channel and contribute to the discussions. If you see a posting for a job, directly message the poster and pitch yourself."
      },
      {
        "header": "📼️ Share a video of your process",
        "idea": "Share a video of your development process from beginning to end. Clients often look for transparency and clarity when selecting who to work with. It can help you stand out and make their decision easier.",
        "actionableTip": "Record a short 3-5 minute informational-style video explaining how you work with clients from the proposal phase to delivery. Upload it to YouTube and list it on your website. Also share it on all your social media accounts, LinkedIn is great for these types of videos."
      },
      {
        "header": "🖼️ Post your portfolio on Dribbble & Behance"
      },
      {
        "header": "👨‍💼️ Reach out to relevant people on LinkedIn cold"
      },
      {
        "header": "🖼️ Build an audience on Instagram"
      },
      {
        "header": "🐤 Build a Twitter following"
      },
      {
        "header": "👍 Engage with relevant Facebook Groups"
      },
      {
        "header": "📌 Pin your work on Pinterest"
      },
      {
        "header": "📤  ️ Share every completed project"
      },
      {
        "header": "✔️ Start conversations on Twitter & slide into DMs"
      },
      {
        "header": "@ Tag popular accounts clients follow in your posts"
      },
      {
        "header": "🏆 Give away your services as part of a contest"
      }
    ]
  },
  {
    "header": "Online Strategy",
    "list": [
      {
        "header": "🗺️ Find local service businesses with bad or no websites",
        "idea": "Target potential clients which are close to where you live and have outdated or broken websites. Your proximity to their location is often an advantage as some clients want to work with freelancers nearby.",
        "actionableTip": "Browse Google Maps for shops or services in your town. Find their website on the listing and evaluate if their site needs work. If it does, put together a quick mock-up of how it could look. Finally, email them a link to the mock up, highlighting that you live nearby and why your improvements would benefit them."
      },
      {
        "header": "👩 Add a short video selfie to your website",
        "idea": "Easily build rapport with by humanising your website with a simple video of yourself. The internet can be an untrustworthy place and business is built upon trust. You'd be surprised how much a real face can draw attention and engage your visitors.",
        "actionableTip": "Record a 15-second clip introducing yourself and set it to auto-play with subtitles on your landing page. Alternately use a service like videoask.com."
      },
      {
        "header": "💬 Ditch contact forms and install a chat tool",
        "idea": "Install a support chat tool on your website so that visitors can instantly get in touch with you. Contact forms are dated and require visitors to fill in a form.",
        "actionableTip": "Install a product like <a href='https://crisp.chat' target='blank'>https://crisp.chat</a> on your website. Install their mobile app and reply to your users as you would with instant messaging app."
      },
      {
        "header": "📈 Optimize your website for search engines (SEO)",
        "idea": "Use an SEO keyword search tool for identify keywords clients are searching for and embed them into your website. This strategy often taken months to be effective but is an organic traffic gold mine if done correctly.",
        "actionableTip": "Head over to <a href='https://neilpatel.com/ubersuggest/' target='blank'>ubersuggest</a> and search for a few relevant keywords. Identify which keywords are easy to rank for and add them to your description and landing page."
      },
      {
        "header": "🎯 Re-brand & target a niche in the market",
        "idea" : "Market & tailor your services toward a niche (e.g. e-commerce, youtubers) to better connect with potential clients and provide better value. Marketing your services to \"everyone\" by yourself is often difficult and dilutes your efforts. Start small, you can always expand later.",
        "actionableTip": "Look through your portfolio of work and figure out if there is a pattern or trend of work you've been doing. Double down on that trend by altering your website & client outreach towards that niche."
      },
      {
        "header": "🌪️ Offer funnel products",
        "idea": "Offer a small product (e.g. web-app tool or e-book) for potential clients to attract them to your services. Offering something of value for free is always a good way of standing out in a crowded space.",
        "actionableTip": "Ask your past clients what kind of problems they've been having in your space. Create a small tool or e-book that will help potential clients and share it across your marketing channels."
      },
      {
        "header": "✒️ Write up successful projects as case studies",
        "idea": "Publish a web page or blog post detailing how you developed a successful project. This gives potential clients a glimpse of your workflow and helps them visualise how are project with you would run.",
        "actionableTip": "Pick 3/4 of your past successful projects and break down the project into several stages. Clearly lay out the problem/goal the client began with and the different steps you took to deliver the project successfully. Images at each stage are great and feature a happy testimonial from your client to wrap it off."
      },
      {
        "header": "📧 Send 50 cold emails"
      },
      {
        "header": "🏪 Use Local SEO tactics"
      }
    ]
  },
  {
    "header": "Freelance Platforms",
    "list": [
      {
        "header": "🤝 Become an official partner of a popular platform",
        "idea": "Apply to become a official development partner for a popular platform such as Square Space or Shopify. Such platforms will list you on their site and drive traffic to your services. Clients are more likely to work with official, vetted freelancers.",
        "actionableTip": "Apply to become a Squarespace Expert: <a href='https://www.squarespace.com/designer/how-it-works' target='blank'>https://www.squarespace.com/designer/how-it-works</a>\n" +
          "\n" +
          "Apply to become a Shopify Official Partner: <a href='https://www.shopify.co.uk/partners' target='blank'>https://www.shopify.co.uk/partners</a>."
      },
      {
        "header": "🗞️ Browse & subscribe to a classified ads platform",
        "idea": "Browse and subscribe to a classified jobs board. They're not the the go-to platform for job listings but companies do often post on them. Consequently competition is a lot less than other platforms.",
        "actionableTip": "Head over to your country's most popular classified ads platform (Craigslist in the USA & Gumtree in the UK). Navigate to the jobs sections and subscribe to updates. Contact any listings that look relevant."
      },
      {
        "header": "⬆️ Apply to Upwork.com"
      },
      {
        "header": "👆️ Apply to FreeUp.net"
      },
      {
        "header": "⌛️ Sign up to PeoplePerHour.com"
      },
      {
        "header": "💻️ Sign up to Freelancer.com"
      },
      {
        "header": "5⃣ Sign up to Fiverr.com as a funnel"
      },
      {
        "header": "👼 Sign up to Angel.co"
      },
      {
        "header": "👊 Sign up to jobs.smashingmagazine.com"
      },
      {
        "header": "🏝️ Subscribe to weworkremotely.com"
      },
      {
        "header": "✍️ Sign up to krop.com"
      },
      {
        "header": "👔️ Sign up to authenticjobs.com"
      },
      {
        "header": "👌 Subscribe to SolidGigs.com"
      },
      {
        "header": "🎨️ Apply to 99Designs.com"
      },
      {
        "header": "🕹️ Subscribe to remote.co"
      },
      {
        "header": "🏠️ Subscribe to dowfh.io"
      }
    ]
  },
  {
    "header": "Content",
    "list": [
      {
        "header": "🖋️ Start a blog",
        "idea": "Content is king in today's online world and there's no easier way to generate content then blogging. Create a blog on your website to drive SEO traffic targeted SEO traffic. Write about things that interest you and would be helpful for clients.",
        "actionableTip": "Write at least one blog post a week about a topic you find interesting and can share with the world. Share each article across social media channels and online communities."
      },
      {
        "header": "📝 Guest Blog",
        "idea": "Write for an existing blog with an audience listing your name, photo and a short description of your services. This helps build your reputation and direct new traffic to your website.",
        "actionableTip": "Note down your favourite blogs or ask your clients for recommendations. Write up a topical article and pitch it to them via their contact addresses. Do this for as many blogs as you can. If successful, share it on all your social media accounts.",
      },
      {
        "header": "🛠️ Build a useful product",
        "idea": "Build a small app/tool solving a small problem your clients have. Good, simple products are organically shared and can promote your services to thousands of new visitors.",
        "actionableTip": "Reach out to your clients and probe for their day to day problems. Build a small tool that saves them a little time or a repetitive task. Share the tool on Product Hunt & Hacker News with a link to your website."
      },
      {
        "header": "💁 Feature as a guest on a podcast or webinar",
        "idea": "Appear as a guest on a podcast or webinar with expertise to share. Podcasts and webinars are quickly becoming a popular medium for content. They typically have a very engaged audience perfect for getting your name out there.",
        "actionableTip": "Head over to <a href='https://podcasts.google.com' target='blank'>https://podcasts.google.com</a> and browse for relevant podcasts. Google their hosts and pitch to them why you should be featured. Pitch a topic where you can share some expertise that their listeners would find useful."
      },
      {
        "header": "🎙️ Start a podcast",
        "idea": "Start a podcast series in your space discussing topics in your space and feature relevant guests. Podcasts are one of the fastest growing content consumption mediums today and also a great way to demonstrate expertise.",
        "actionableTip": "Sign up for a free account at anchor.fm and record your first podcast with your laptop mic. Pick a topic you're passionate about and record a short 10-15 minute episode. Anchor.fm will also distribute your podcaste across networks such as Spotify so you can easily share a link to the series across your marketing channels."
      },
      {
        "header": "🎥 Start a YouTube channel"
      },
      {
        "header": "📗 Write an ebook"
      },
      {
        "header": "📨 Start an email newsletter"
      }
    ]
  },
  {
    "header": "Engineering",
    "list": [
      {
        "header": "🆓 Work on a project pro bono",
        "idea": "If you're just starting out and don't have a portfolio, work pro bono (free) on a small project in exchange for listing your name in the website footer and a testimonial that you can share on your website.",
        "actionableTip": "Advertise on Reddit or an equivalent community that you're available for x hours to work on a small project in exchange for credit in the footer & a testimonial.",
      },
      {
        "header": "🔨 Fix an existing website",
        "idea": "When you come across a website where something is broken, investigate and consider how you could fix the issue. We often find issues with websites online, pro-actively helping a client fix an issue can build great rapport.",
        "actionableTip": "The next time you find something broken on a website, spend a little time investigating the issue and planning a fix. Email the contact address highlighting the issue along with your proposed solution.",
      },
      {
        "header": "💅️ Revamp an existing site",
        "idea": "When you see a website that looks outdated, think about how you can improve upon it to bring it up-to-date. Email the owner with your thoughts and aim to start a conversation about a project to update it.",
        "actionableTip": "The next time you come across a website that looks dated, check if its mobile responsive via the Chrome dev tools. If its not, spend 30 mins mocking up a mobile version of the landing page. Then email the owner the mock-up and ask whether they're interested in improving it for mobile too.",
      },
      {
        "header": "⌨️ Contribute to an open source project",
        "idea": "If you're a software engineer, build your up reputation by contributing to an open source project. OS projects are a great way to sharpen your skills and connect with fellow engineers who could have paid work to be done.",
        "actionableTip": "Look through your code for small to medium sized open source libraries you have used. They're more likely to need help and accept newcomers. If on github, head over to the 'issues' section of the repo and see if you can help them resolve any. Just raise a pull request with your fix to help out."
      },
      {
        "header": "🏷️ Add a \"Designed by x\" label to footers",
        "idea": "If you're a web developer/designer, add a small text to footer of a project you've completed which links to your website. Clients who like a website often scroll to the footer to find out who it was designed by, it's a free showcase. Additionally, it provides SEO backlinks which boost your website's credibility.",
        "actionableTip":"Ask your client if they would be happy with you adding a small line of text to the footer of a site. If you have a great relationship with them you'll be surprised how much they will support this. Alternatively, offer a small discount off your fee to add this label, it will pay off the long run."
      },
      {
        "header": "👨‍🎨️ Offer your take on an existing website"
      },
    ]
  },
  {
    "header": "Offline",
    "list": [
      {
        "header": "🏙️ Join a co-working space",
        "idea": "Rent a hot desk at your local co-working space to meet other liked minded entrepreneurs. They're often filled with small companies that are looking to hire freelancers for projects. In-person connections result in a higher conversion rate than online connections.",
        "actionableTip": "Join a local co-working space and slowly introduce yourself to everyone around you. Find out they're working on and tell them about your services. Say hello when you see them and develop a professional relationship so they can send work your way when you need it.",
      },
      {
        "header": "🏡 Attend Meetups in your area",
        "idea": "Attend a few popular Meetups in your area to meet potential clients in person. Understand what they are trying to do, rather than promote your services.",
        "actionableTip": "Head to <a href='https://meetup.com' target='blank'>https://meetup.com</a> and search for upcoming events in the Entrepreneurship category. Subscribe to the Meetup group and introduce yourself online. Attend their next event and say hello to as many people as you can. Ask questions rather than talk about your services.",
      },
      {
        "header": "📇 Leave your business card behind",
        "idea": "Supermarkets, libraries and small businesses often have billboards to pin your business card. They receive a lot of foot traffic and someone may see your card there when looking for a service.",
        "actionableTip": "Design a unique business card that looks different to the typical card to stand out and grab someone's attention. Head to your local supermarkets and libraries and pin your card there. If you don't see a billboard, enquire about one. Always keep a few handy in your bag in case you find a new billboard.",
      },
      {
        "header": "🎤 Speak at a conference or event",
        "idea": "Offer your expertise on a topic for free by speaking at a conference or webinar. This builds you credibility and provides free marketing for your name/brand through the conference's marketing campaign",
        "actionableTip": "Ask via your status on LinkedIn or Google for conferences in your area of work. Navigate to their contact information and write an email to them explaining your background and why they should let you speak in the conference. It's best to pitch a specific trending topic to talk about that you have researched/experience with."
      },
      {
        "header": "📞 Cold call 10 potential clients on the phone"
      }
    ]
  }
];
